import React, {useState} from "react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from '@tanstack/react-query';
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";

const AreaEditor = ({area, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: area ? area.name : '',
        description: area ? area.description : '',
        color: area ? area.color : '#4DB6AC',
        icon: area ? area.icon : 'home',
        displayOrder: area ? area.displayOrder : 1,
        active: area ? area.active : true
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const queryClient = useQueryClient();

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom de la zone est requis";
        }

        if (!formData.color.trim()) {
            newErrors.color = "La couleur est requise";
        }

        if (!formData.icon.trim()) {
            newErrors.icon = "L'icône est requise";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const payload = {
                name: formData.name,
                description: formData.description,
                color: formData.color,
                icon: formData.icon,
                displayOrder: parseInt(formData.displayOrder) || 1,
                active: formData.active
            };

            if (area) {
                // Update existing area
                await api.put(`/areas/${area.id}`, payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Zone mise à jour avec succès');
            } else {
                // Create new area
                await api.post('/areas', payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Zone créée avec succès');
            }

            queryClient.invalidateQueries({queryKey: ['areas']});
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={area ? 'Modifier la zone' : 'Nouvelle zone'}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="area-form" saving={submitting}
                                 label={area ? 'Enregistrer' : 'Créer'}/>}
        >
            <form id="area-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="Ex: Intérieur"
                        disabled={submitting}
                    />
                </FormField>

                <FormField id="description" label="Description">
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="3"
                        className={`${inputClass(false)} resize-none`}
                        placeholder="Description de la zone"
                        disabled={submitting}
                    />
                </FormField>

                <FormField id="color" label="Couleur" required error={errors.color}>
                    <div className="flex gap-2">
                        <input
                            type="color"
                            id="color"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            className="h-11 w-16 flex-shrink-0 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-white dark:bg-gray-800"
                            disabled={submitting}
                        />
                        <input
                            type="text"
                            aria-label="Code couleur"
                            value={formData.color}
                            onChange={(e) => setFormData(prev => ({...prev, color: e.target.value}))}
                            className={inputClass(errors.color)}
                            placeholder="#4DB6AC"
                            disabled={submitting}
                        />
                    </div>
                </FormField>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField id="icon" label="Icône" required error={errors.icon}>
                        <input
                            type="text"
                            id="icon"
                            name="icon"
                            value={formData.icon}
                            onChange={handleChange}
                            className={inputClass(errors.icon)}
                            placeholder="Ex: home, restaurant, terrace"
                            disabled={submitting}
                        />
                    </FormField>

                    <FormField id="displayOrder" label="Ordre d'affichage">
                        <input
                            type="number"
                            id="displayOrder"
                            name="displayOrder"
                            value={formData.displayOrder}
                            onChange={handleChange}
                            min="1"
                            className={inputClass(false)}
                            disabled={submitting}
                        />
                    </FormField>
                </div>

                <SwitchRow id="active" label="Zone active" checked={formData.active} disabled={submitting}
                           onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
            </form>
        </Modal>
    );
};

export default AreaEditor;

